import { useSymbolIcon } from "@/shared/lib/hooks/useSymbolIcon";
import { getPairMaxPayoutPercent } from "@/types/index";
import type { PairMessage } from "@cronika/proto/common/trading_pb";
import { Star, StarOutline } from "@cronika/assets/icons";
import { cn } from "@cronika/ui";
import type { FC } from "react";

type TPairRowProps = {
  pair: PairMessage;
  gridCols: string;
  isFavorite: boolean;
  isSelected: boolean;
  isActivePair: boolean;
  onSelect: (pair: PairMessage) => void;
  onToggleFavorite?: (pair: PairMessage) => void;
};

export const PairRow: FC<TPairRowProps> = ({
  pair,
  gridCols,
  isFavorite,
  isActivePair,
  onSelect,
  onToggleFavorite,
}) => {
  const icon = useSymbolIcon(pair.type, pair.baseAsset, pair.quoteAsset);
  const payoutPercent = getPairMaxPayoutPercent(pair);
  const isInactive = pair.isTradingEnabled === false;
  const displaySymbol = pair.isOtc ? pair.symbol.replace(/\.OTC$/i, "") : pair.symbol;

  return (
    <button
      type="button"
      onClick={() => !isInactive && onSelect(pair)}
      className={cn(
        gridCols,
        "w-full rounded-lg py-2.5 text-sm transition-colors select-text my-2 pl-[3px] pr-[14px]",
        isInactive ? "opacity-50 cursor-default" : "hover:bg-[#FFFFFF0F]",
        isActivePair && "bg-[#8C8C8C]/15 border border-[#8C8C8C]/15 ",
      )}
    >
      {/* Star / favourite */}
      <span
        role="button"
        tabIndex={-1}
        onClick={(e) => {
          e.stopPropagation();
          onToggleFavorite?.(pair);
        }}
        className="flex items-center justify-center rounded transition-colors"
      >
        {isFavorite ? <Star className="h-4 w-4 text-warning" /> : <StarOutline className="h-4 w-4 text-white/[0.28]" />}
      </span>

      {/* Pair icon */}
      {icon ? (
        <img src={icon} alt={pair.symbol} className="ml-[3px] h-[22px] w-9 object-contain" />
      ) : (
        <span className="flex h-[22px] w-[22px] items-center justify-center rounded-full bg-default-200 text-[8px] font-medium">
          {pair.baseAsset.slice(0, 2)}
        </span>
      )}

      {/* Symbol + OTC badge */}
      <span className="ml-[8px] flex items-center gap-1.5 text-left min-w-0">
        <span className="truncate text-[13px] text-white/90 font-semibold">{displaySymbol}</span>
        {pair.isOtc && (
          <span className="shrink-0 rounded px-1 py-px text-[9px] font-semibold uppercase tracking-wide bg-[#F4A425]/15 text-[#F4A425]">
            OTC
          </span>
        )}
      </span>

      {/* Payout */}
      {isInactive ? (
        <span className="flex items-center justify-end gap-1 text-sm font-medium text-[#4A4A4A]">
          <svg width="12" height="12" viewBox="0 0 14 14" fill="none" className="shrink-0">
            <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.5" />
            <line x1="2.5" y1="11.5" x2="11.5" y2="2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          N/A
        </span>
      ) : (
        <span className="text-right text-[13px] font-semibold text-[#17C964]">{payoutPercent}%</span>
      )}
    </button>
  );
};
